/**
 * The chosen tenant's look, in the console (2026-10-20).
 *
 * Until now only the client portal applied `tenant_branding`; the console
 * always showed CosMon's orange, so Element Security's owner signed in and saw
 * someone else's product. The colour maths is `src/branding/colours.ts`, the
 * same file the portal and the server use; this file only decides which
 * variables go where and that a previous tenant's never linger.
 *
 * DOM-free apart from the `style` it is handed, so it can be tested directly.
 */

import {
  CONSOLE_BRAND_PROPERTIES,
  CONSOLE_DARK_PAGE,
  CONSOLE_LIGHT_PAGE,
  consoleBrandVariables,
} from '../../../src/branding/colours.ts';

/** `GET /api/tenant/console-branding`. */
export interface ConsoleBranding {
  /** `tenants.name`, not `tenant_branding.product_name` (Element's is still 'COSMON'). */
  tenantName: string;
  colorPrimary: string | null;
  colorAccent: string | null;
  colorHeader: string | null;
  logoUrl: string | null;
}

interface StyleTarget {
  setProperty(name: string, value: string): void;
  removeProperty(name: string): string;
}

/**
 * Write a tenant's variables onto `:root`'s inline style, after clearing every
 * variable a brand can write. Null (no tenant, no branding, a failed fetch)
 * leaves the stylesheet's own CosMon values in force.
 *
 * The page is taken from the THEME rather than read back from the computed
 * `--bg`: the shell's effect runs before the one that flips `data-theme`, so a
 * read-back would judge the new theme's colours against the old page.
 */
export function applyConsoleBrand(style: StyleTarget, branding: ConsoleBranding | null, theme: string): void {
  for (const name of CONSOLE_BRAND_PROPERTIES) style.removeProperty(name);
  if (branding === null) return;
  const vars = consoleBrandVariables(
    { primary: branding.colorPrimary, accent: branding.colorAccent, header: branding.colorHeader },
    theme === 'light' ? CONSOLE_LIGHT_PAGE : CONSOLE_DARK_PAGE,
  );
  if (vars === null) return;
  for (const [name, value] of Object.entries(vars)) style.setProperty(name, value);
}

/**
 * What the rail's brand block shows: the tenant's logo, on its header colour
 * when it has one, and always the tenant's name — a logo is not a name, and an
 * operator serving several customers reads the name.
 */
export function brandBlock(
  branding: ConsoleBranding | null,
  tenantName: string,
): { logoUrl: string | null; plated: boolean; name: string } {
  const logoUrl = branding?.logoUrl ?? null;
  return { logoUrl, plated: logoUrl !== null && branding?.colorHeader != null, name: tenantName };
}

/**
 * The browser tab: the tenant's name once it has a look of its own in the
 * console (a primary colour or a logo), COSMON otherwise. An accent alone
 * changes nothing here, so it does not rename the tab either.
 */
export function consoleTitle(branding: ConsoleBranding | null): string {
  if (branding === null) return 'COSMON';
  const branded = branding.colorPrimary !== null || branding.logoUrl !== null;
  return branded && branding.tenantName.trim() !== '' ? branding.tenantName : 'COSMON';
}

/**
 * Fired by Settings › Client portal after a save, so the console re-brands at
 * once rather than on the next tenant switch or reload.
 */
export const BRANDING_CHANGED = 'cosmon:branding-changed';
